import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import DomainSearchConsole from '@/components/DomainSearchConsole';
import { ChangeVerification, GoogleSearchEvidence } from '@/components/HealthEvidence';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from 'sonner';
import { ContactButton } from '@/components/ContactForm';
import { AppLayout } from '@/components/AppLayout';
import { 
  FileText, TrendingUp, LayoutDashboard,
  Plus, Crown, Zap, CheckCircle, Search, Bot, ShieldCheck, Shield,
  ArrowUpRight, ArrowDownRight, Minus, Globe, BarChart3,
  FileSearch, Code, Type, AlignLeft, Image, FileWarning, Calendar, Trash2,
  Layers, Phone, Brain, Copy, XCircle, Lock, Gauge, Unlink, Sparkles, RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── ANIMATION HOOKS ────────────────────────────────────────────────────────

// Count-up animation hook with IntersectionObserver
function useCountUp(endValue, duration = 800, trigger = null) {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);
  const prevTrigger = useRef(trigger);

  useEffect(() => {
    // Reset animation when trigger changes (e.g., domain/filter change)
    if (trigger !== prevTrigger.current) {
      setHasAnimated(false);
      setCount(0);
      prevTrigger.current = trigger;
    }
  }, [trigger]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          const startTime = performance.now();
          const startValue = 0;
          
          const animate = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            
            // Easing function (ease-out-cubic)
            const easeOut = 1 - Math.pow(1 - progress, 3);
            const currentValue = Math.round(startValue + (endValue - startValue) * easeOut);
            
            setCount(currentValue);
            
            if (progress < 1) {
              requestAnimationFrame(animate);
            }
          };
          
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [endValue, duration, hasAnimated]);

  return { count, ref, hasAnimated };
}

// Time range options
const TIME_RANGES = [
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '12m', label: 'Last 12 months' },
  { value: 'all', label: 'All time' },
];

// KPI Definitions - dynamic: prefers business_analysis scores when available
const KPI_CONFIG_BUSINESS = [
  { key: 'overview_score', label: 'Overview', icon: Globe, color: 'blue', suffix: '', higherBetter: true },
  { key: 'seo_score', label: 'SEO Score', icon: Search, color: 'blue', suffix: '', higherBetter: true },
  { key: 'ai_readiness_score', label: 'AI Readiness', icon: Brain, color: 'purple', suffix: '', higherBetter: true },
  { key: 'trust_score', label: 'Trust', icon: ShieldCheck, color: 'green', suffix: '', higherBetter: true },
  { key: 'content_score', label: 'Content', icon: FileText, color: 'teal', suffix: '', higherBetter: true },
  { key: 'contact_score', label: 'Contact', icon: Phone, color: 'green', suffix: '', higherBetter: true },
  { key: 'coverage_score', label: 'Coverage', icon: Layers, color: 'cyan', suffix: '', higherBetter: true },
  { key: 'indexing_score', label: 'Indexing', icon: FileSearch, color: 'orange', suffix: '', higherBetter: true },
  { key: 'actions_p1_count', label: 'P1 Actions', icon: Zap, color: 'red', suffix: '', higherBetter: false },
  { key: 'pages_scanned', label: 'Pages', icon: BarChart3, color: 'yellow', suffix: '', higherBetter: true },
  { key: 'security_score', label: 'Security', icon: Lock, color: 'green', suffix: '', higherBetter: true },
  { key: 'geo_visibility_score', label: 'AI Visibility', icon: Sparkles, color: 'purple', suffix: '%', higherBetter: true },
  { key: 'performance_score', label: 'Performance', icon: Gauge, color: 'cyan', suffix: '', higherBetter: true },
  { key: 'broken_links_count', label: 'Broken Links', icon: Unlink, color: 'red', suffix: '', higherBetter: false },
];

const KPI_CONFIG_LEGACY = [
  { key: 'seo_score', label: 'SEO Score', icon: Search, color: 'blue', suffix: '', higherBetter: true },
  { key: 'ai_readiness_score', label: 'AI Readiness', icon: Bot, color: 'purple', suffix: '', higherBetter: true },
  { key: 'trust_evidence_score', label: 'Trust Score', icon: ShieldCheck, color: 'green', suffix: '', higherBetter: true },
  { key: 'indexable_percent', label: 'Indexable', icon: FileSearch, color: 'cyan', suffix: '%', higherBetter: true },
  { key: 'schema_presence_percent', label: 'Schema', icon: Code, color: 'orange', suffix: '%', higherBetter: true },
  { key: 'missing_title_count', label: 'Missing Titles', icon: Type, color: 'red', suffix: '', higherBetter: false },
  { key: 'missing_meta_desc_count', label: 'Missing Meta', icon: AlignLeft, color: 'red', suffix: '', higherBetter: false },
  { key: 'average_word_count', label: 'Avg Words', icon: FileText, color: 'teal', suffix: '', higherBetter: true },
  { key: 'thin_pages_count', label: 'Thin Pages', icon: FileWarning, color: 'yellow', suffix: '', higherBetter: false },
  { key: 'images_missing_alt_percent', label: 'Missing Alt', icon: Image, color: 'red', suffix: '%', higherBetter: false },
  { key: 'security_score', label: 'Security', icon: Lock, color: 'green', suffix: '', higherBetter: true },
  { key: 'geo_visibility_score', label: 'AI Visibility', icon: Sparkles, color: 'purple', suffix: '%', higherBetter: true },
  { key: 'performance_score', label: 'Performance', icon: Gauge, color: 'cyan', suffix: '', higherBetter: true },
  { key: 'broken_links_count', label: 'Broken Links', icon: Unlink, color: 'red', suffix: '', higherBetter: false },
];

const COLOR_MAP = {
  blue: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/30' },
  purple: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/30' },
  green: { bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/30' },
  cyan: { bg: 'bg-cyan-500/10', text: 'text-cyan-400', border: 'border-cyan-500/30' },
  orange: { bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/30' },
  red: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/30' },
  teal: { bg: 'bg-teal-500/10', text: 'text-teal-400', border: 'border-teal-500/30' },
  yellow: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', border: 'border-yellow-500/30' },
};

export default function DashboardPage() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [domainTab, setDomainTab] = useState('overview');
  const [domainEvidence, setDomainEvidence] = useState(null);
  const [evidenceError, setEvidenceError] = useState(null);
  const evidenceRequest = useRef(0);
  const activeEvidenceDomain = useRef(null);
  const gscCallbackHandled = useRef(false);
  const [selectedMetric, setSelectedMetric] = useState('overview_score');
  const [timeRange, setTimeRange] = useState('30d');
  const [deleteAudit, setDeleteAudit] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [clearInactiveOpen, setClearInactiveOpen] = useState(false);
  const [clearingInactive, setClearingInactive] = useState(false);

  const evidenceDomain = data?.kpi?.domains?.find(item => item.domain?.toLowerCase() === selectedDomain)?.domain;
  const evidenceAuditId = data?.kpi?.domains?.find(item => item.domain?.toLowerCase() === selectedDomain)?.latest?.audit_id;
  activeEvidenceDomain.current = evidenceDomain;
  const loadDomainEvidence = useCallback(async () => {
    if (activeEvidenceDomain.current !== evidenceDomain) return;
    const request = ++evidenceRequest.current;
    if (!evidenceDomain) return;
    try {
      const response = await axios.get(`${API}/gsc/domains/${encodeURIComponent(evidenceDomain)}`);
      if (request === evidenceRequest.current && activeEvidenceDomain.current === evidenceDomain) { setDomainEvidence(response.data); setEvidenceError(null); }
    } catch {
      if (request === evidenceRequest.current && activeEvidenceDomain.current === evidenceDomain) setEvidenceError('Domain evidence could not be loaded. Retry to load stored data.');
    }
  }, [evidenceDomain]);
  useEffect(() => {
    setDomainEvidence(null); setEvidenceError(null);
    loadDomainEvidence();
    return () => { evidenceRequest.current += 1; };
  }, [loadDomainEvidence, evidenceAuditId]);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/dashboard`);
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAudit = async () => {
    if (!deleteAudit) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/audit/${deleteAudit.id}`);
      toast.success('Audit deleted');
      setDeleteAudit(null);
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete audit');
    } finally {
      setDeleting(false);
    }
  };

  const [reauditing, setReauditing] = useState(null);
  const handleReaudit = async (auditId) => {
    if (reauditing) return;
    setReauditing(auditId);
    try {
      const res = await axios.post(`${API}/audit/${auditId}/reaudit`);
      toast.success('Re-audit started — tracking progress');
      window.location.href = `/audit/${res.data.audit_id}`;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start re-audit');
      setReauditing(null);
    }
  };

  const handleClearInactive = async () => {
    setClearingInactive(true);
    try {
      const res = await axios.delete(`${API}/domains/clear-inactive`);
      const count = res.data.archived_count || 0;
      if (count > 0) {
        toast.success(`Cleared ${count} inactive domain${count > 1 ? 's' : ''}`);
        fetchDashboard();
      } else {
        toast.info('No inactive domains to clear');
      }
      setClearInactiveOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clear inactive domains');
    } finally {
      setClearingInactive(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) return;
    setCheckingPayment(true);
    try {
      const res = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        toast.success('Payment successful!');
        fetchDashboard();
        setCheckingPayment(false);
        window.history.replaceState({}, '', '/dashboard');
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  };

  useEffect(() => {
    if (!authLoading && user) {
      fetchDashboard();
    } else if (!authLoading && !user) {
      setLoading(false);
    }
    
    const sessionId = searchParams.get('session_id');
    const success = searchParams.get('success');
    if (sessionId && success === 'true') {
      pollPaymentStatus(sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, authLoading, user]);

  useEffect(() => {
    if (gscCallbackHandled.current || !data?.kpi?.domains?.length) return;
    const gscStatus = searchParams.get('gsc');
    if (!gscStatus) return;
    gscCallbackHandled.current = true;
    const callbackDomain = searchParams.get('gsc_domain')?.toLowerCase();
    const domains = data.kpi.domains.map(d => d.domain?.toLowerCase());
    if (callbackDomain && domains.includes(callbackDomain)) {
      setSelectedDomain(callbackDomain);
      setDomainTab('search');
    }
    if (gscStatus === 'connected') {
      toast.success('Google Search Console connected');
    } else {
      toast.error(searchParams.get('gsc_detail') || 'Google Search Console connection failed');
    }
    window.history.replaceState({}, '', '/dashboard');
  }, [data, searchParams]);

  useEffect(() => {
    if (data?.kpi?.domains?.length > 0) {
      // Normalize and dedupe domains
      const domains = data.kpi.domains.reduce((acc, d) => {
        const normalizedId = d.domain.toLowerCase();
        if (!acc.find(x => x.domain.toLowerCase() === normalizedId)) {
          acc.push({ ...d, domain_id: normalizedId });
        }
        return acc;
      }, []);
      
      // Check if current selectedDomain still exists in new data
      const currentExists = selectedDomain && domains.find(d => d.domain_id === selectedDomain);
      
      if (!currentExists) {
        // Select primary or first domain
        const primary = domains.find(d => d.is_primary) || domains[0];
        if (primary) {
          setSelectedDomain(primary.domain_id);
        } else {
          setSelectedDomain(null);
        }
      }
    } else if (data && !data?.kpi?.domains?.length) {
      // No domains left
      setSelectedDomain(null);
    }
  }, [data, selectedDomain]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-10 w-64 bg-[#1A1A1A]" />
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[...Array(10)].map((_, i) => <Skeleton key={i} className="h-24 bg-[#1A1A1A]" />)}
          </div>
        </div>
      </div>
    );
  }

  const subscription = data?.subscription || {};
  const tierInfo = subscription.tier_info || {};
  const stats = data?.stats || {};
  const kpi = data?.kpi || {};
  const effectiveTier = isAdmin ? 'admin' : subscription.tier;
  const effectiveCredits = isAdmin ? '∞' : (subscription.credits_remaining || 0);
  
  // Normalize domain names (lowercase) and dedupe
  const normalizedDomains = kpi.domains?.reduce((acc, d) => {
    const normalizedId = d.domain.toLowerCase();
    if (!acc.find(x => x.domain.toLowerCase() === normalizedId)) {
      acc.push({ ...d, domain_id: normalizedId, domain: d.domain.toLowerCase() });
    }
    return acc;
  }, []) || [];
  
  const selectedDomainData = normalizedDomains.find(d => d.domain_id === selectedDomain);
  
  // Filter audits by selected domain
  const filteredAudits = selectedDomain && data?.audits
    ? data.audits.filter(a => {
        const auditDomain = a.target_url?.replace(/^https?:\/\/(www\.)?/, '').split('/')[0]?.toLowerCase();
        return auditDomain === selectedDomain;
      })
    : data?.audits || [];

  // Animated change indicator with micro-animation
  const ChangeIndicator = ({ value, higherBetter, animate = false }) => {
    const [visible, setVisible] = useState(!animate);
    
    useEffect(() => {
      if (animate) {
        const timer = setTimeout(() => setVisible(true), 300);
        return () => clearTimeout(timer);
      }
    }, [animate]);
    
    if (value === undefined || value === null || value === 0) return null;
    const isGood = higherBetter ? value > 0 : value < 0;
    const Icon = value > 0 ? ArrowUpRight : ArrowDownRight;
    return (
      <span 
        className={`text-xs flex items-center gap-0.5 transition-all duration-500 ${
          isGood ? 'text-green-400' : 'text-red-400'
        } ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
      >
        <Icon className="w-3 h-3" />
        {value > 0 ? '+' : ''}{value}
      </span>
    );
  };

  // Filter timeline by time range
  const filterTimelineByRange = (timeline) => {
    if (!timeline || timeline.length === 0) return [];
    if (timeRange === 'all') return timeline;
    
    const now = new Date();
    let cutoffDate;
    
    switch (timeRange) {
      case '30d':
        cutoffDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case '90d':
        cutoffDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      case '12m':
        cutoffDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
        break;
      default:
        return timeline;
    }
    
    return timeline.filter(t => new Date(t.date) >= cutoffDate);
  };

  // Interactive line chart with tooltips
  // ── Timeline + Bar Chart with Animations ────────────────────────────────────
  const BarChart = ({ data, metricKey, color, range }) => {
    const [hovered, setHovered] = useState(null);
    const [isVisible, setIsVisible] = useState(false);
    const [animationStarted, setAnimationStarted] = useState(false);
    const chartRef = useRef(null);

    // IntersectionObserver for triggering animations
    useEffect(() => {
      const element = chartRef.current;
      if (!element) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !animationStarted) {
            setAnimationStarted(true);
            // Small delay before starting animation
            setTimeout(() => setIsVisible(true), 100);
          }
        },
        { threshold: 0.2 }
      );

      observer.observe(element);
      return () => observer.disconnect();
    }, [animationStarted]);

    // Reset animation when data changes
    useEffect(() => {
      setIsVisible(false);
      setAnimationStarted(false);
      const timer = setTimeout(() => {
        if (chartRef.current) {
          setAnimationStarted(true);
          setTimeout(() => setIsVisible(true), 100);
        }
      }, 50);
      return () => clearTimeout(timer);
    }, [data, metricKey, range]);

    const colorHex = {
      blue: '#3b82f6', purple: '#a855f7', green: '#22c55e', cyan: '#06b6d4',
      orange: '#f97316', red: '#ef4444', teal: '#14b8a6', yellow: '#eab308',
    }[color] || '#3b82f6';

    const colorBg = {
      blue: 'bg-blue-500', purple: 'bg-purple-500', green: 'bg-green-500',
      cyan: 'bg-cyan-500', orange: 'bg-orange-500', red: 'bg-red-500',
      teal: 'bg-teal-500', yellow: 'bg-yellow-500',
    }[color] || 'bg-blue-500';

    // ── Build slots based on range ──────────────────────────────────────────
    const buildSlots = () => {
      const now = new Date();
      now.setHours(23, 59, 59, 999);

      // Find the earliest data point
      const sortedData = [...(data || [])].filter(e => e.date).sort((a, b) => new Date(a.date) - new Date(b.date));
      const firstDataDate = sortedData.length > 0 ? new Date(sortedData[0].date) : new Date();
      firstDataDate.setHours(0, 0, 0, 0);

      if (range === '30d' || range === '90d') {
        const days = range === '30d' ? 30 : 90;
        // Start from first data point, extend forward for 'days' slots
        const slots = Array.from({ length: days }, (_, i) => {
          const d = new Date(firstDataDate);
          d.setDate(d.getDate() + i);
          d.setHours(0, 0, 0, 0);
          return { label: d.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' }), date: d, fullDate: d, values: [], isFuture: d > now };
        });
        // Map data points into slots - keep track of latest entry per slot
        (data || []).forEach(entry => {
          if (entry[metricKey] == null) return;
          const ed = new Date(entry.date);
          const idx = slots.findIndex(s => {
            const next = new Date(s.date); next.setDate(next.getDate() + 1);
            return ed >= s.date && ed < next;
          });
          if (idx >= 0) {
            // Store entry with its date for finding the latest one
            if (!slots[idx].entries) slots[idx].entries = [];
            slots[idx].entries.push({ value: Number(entry[metricKey]) || 0, date: ed });
          }
        });
        // Use the LATEST (most recent) entry for each slot, not average
        return slots.map(s => {
          if (!s.entries || s.entries.length === 0) {
            return { ...s, value: null, hasData: false, fullDate: s.date };
          }
          // Sort entries by date descending and take the latest
          const sortedEntries = s.entries.sort((a, b) => b.date - a.date);
          const latestEntry = sortedEntries[0];
          return {
            ...s,
            value: latestEntry.value,
            hasData: true,
            fullDate: latestEntry.date,
          };
        });

      } else if (range === '12m') {
        // Start from first data month, extend forward for 12 months
        const firstMonth = new Date(firstDataDate.getFullYear(), firstDataDate.getMonth(), 1);
        const slots = Array.from({ length: 12 }, (_, i) => {
          const d = new Date(firstMonth.getFullYear(), firstMonth.getMonth() + i, 1);
          return {
            label: d.toLocaleDateString('pl-PL', { month: 'short', year: '2-digit' }),
            date: d,
            fullDate: d,
            entries: [],
            isFuture: d > now,
          };
        });
        (data || []).forEach(entry => {
          if (entry[metricKey] == null) return;
          const ed = new Date(entry.date);
          const idx = slots.findIndex(s => s.date.getFullYear() === ed.getFullYear() && s.date.getMonth() === ed.getMonth());
          if (idx >= 0) {
            slots[idx].entries.push({ value: Number(entry[metricKey]) || 0, date: ed });
          }
        });
        // Use the LATEST (most recent) entry for each month
        return slots.map(s => {
          if (!s.entries || s.entries.length === 0) {
            return { ...s, value: null, hasData: false, fullDate: s.date };
          }
          const sortedEntries = s.entries.sort((a, b) => b.date - a.date);
          const latestEntry = sortedEntries[0];
          return {
            ...s,
            value: latestEntry.value,
            hasData: true,
            fullDate: latestEntry.date,
          };
        });

      } else {
        // "all" — one slot per data point (sorted chronologically)
        return sortedData
          .filter(e => e[metricKey] != null)
          .map(e => ({
            label: new Date(e.date).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' }),
            fullDate: new Date(e.date),
            date: new Date(e.date),
            value: Number(e[metricKey]) || 0,
            hasData: true,
            isFuture: false,
          }));
      }
    };

    const slots = buildSlots();
    const filled = slots.filter(s => s.hasData);

    if (filled.length === 0) {
      return <div className="h-32 flex items-center justify-center text-zinc-500 text-sm">No data for the selected range</div>;
    }

    const values = slots.map(s => s.value ?? 0);
    const maxVal = Math.max(...values, 1);
    const chartH = 80;

    // ── Line path for filled slots ──────────────────────────────────────────
    const totalSlots = slots.length;
    const filledPoints = slots
      .map((s, i) => s.hasData ? { x: i, y: s.value } : null)
      .filter(Boolean);

    // SVG dimensions
    const svgW = 600;
    const svgH = 60;
    const px = (i) => (i / Math.max(totalSlots - 1, 1)) * svgW;
    const py = (v) => svgH - Math.max(2, Math.round((v / maxVal) * svgH));

    const lineD = filledPoints.length > 1
      ? filledPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${px(p.x)} ${py(p.y)}`).join(' ')
      : null;

    const areaD = lineD
      ? `${lineD} L ${px(filledPoints[filledPoints.length - 1].x)} ${svgH} L ${px(filledPoints[0].x)} ${svgH} Z`
      : null;

    // ── Which labels to show (max ~6 visible) ──────────────────────────────
    const showLabel = (i) => {
      if (totalSlots <= 7) return true;
      if (totalSlots <= 31) return i === 0 || i === totalSlots - 1 || i % Math.ceil(totalSlots / 5) === 0;
      return i === 0 || i === totalSlots - 1 || i % Math.ceil(totalSlots / 4) === 0;
    };

    // Calculate line path length for animation
    const lineLength = filledPoints.length > 1 
      ? filledPoints.reduce((acc, p, i) => {
          if (i === 0) return 0;
          const prev = filledPoints[i - 1];
          const dx = px(p.x) - px(prev.x);
          const dy = py(p.y) - py(prev.y);
          return acc + Math.sqrt(dx * dx + dy * dy);
        }, 0)
      : 0;

    return (
      <div className="px-2 pb-1 select-none" ref={chartRef}>

        {/* SVG line (trend) with draw animation */}
        {lineD && (
          <svg width="100%" height={svgH} viewBox={`0 0 ${svgW} ${svgH}`} className="w-full mb-1" preserveAspectRatio="none">
            <defs>
              <linearGradient id={`area-${metricKey}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={colorHex} stopOpacity="0.25" />
                <stop offset="100%" stopColor={colorHex} stopOpacity="0" />
              </linearGradient>
            </defs>
            {/* Area fill with fade-in */}
            {areaD && (
              <path 
                d={areaD} 
                fill={`url(#area-${metricKey})`}
                style={{
                  opacity: isVisible ? 1 : 0,
                  transition: 'opacity 0.8s ease-out 0.3s'
                }}
              />
            )}
            {/* Animated line draw */}
            <path 
              d={lineD} 
              fill="none" 
              stroke={colorHex} 
              strokeWidth="2" 
              strokeLinejoin="round"
              strokeLinecap="round"
              style={{
                strokeDasharray: lineLength,
                strokeDashoffset: isVisible ? 0 : lineLength,
                transition: `stroke-dashoffset 1s ease-out`
              }}
            />
            {/* Animated dots */}
            {filledPoints.map((p, i) => (
              <circle 
                key={i} 
                cx={px(p.x)} 
                cy={py(p.y)} 
                r="4" 
                fill={colorHex}
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'scale(1)' : 'scale(0)',
                  transformOrigin: `${px(p.x)}px ${py(p.y)}px`,
                  transition: `opacity 0.3s ease-out ${0.5 + i * 0.1}s, transform 0.3s ease-out ${0.5 + i * 0.1}s`
                }}
              />
            ))}
          </svg>
        )}

        {/* Bars with staggered grow animation */}
        <div className="flex items-end gap-px" style={{ height: `${chartH}px` }}>
          {slots.map((slot, i) => {
            const barH = slot.hasData ? Math.max(3, Math.round((slot.value / maxVal) * chartH)) : 0;
            const isHov = hovered === i;
            // Stagger delay: spread across 0.6s total
            const staggerDelay = (i / Math.max(slots.length - 1, 1)) * 0.6;
            return (
              <div
                key={i}
                className="flex-1 flex flex-col items-center justify-end cursor-pointer relative"
                style={{ height: '100%' }}
                onMouseEnter={() => slot.hasData && setHovered(i)}
                onMouseLeave={() => setHovered(null)}
              >
                {/* Tooltip */}
                {isHov && slot.hasData && (
                  <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 z-10 px-2 py-1 rounded text-xs font-bold text-white bg-[#1A1A1A] border border-white/20 whitespace-nowrap pointer-events-none shadow-lg text-center">
                    <div className="font-bold">{slot.value}</div>
                    <div className="text-zinc-400 font-normal text-[10px]">
                      {slot.fullDate 
                        ? `${slot.fullDate.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' })} ${slot.fullDate.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}`
                        : slot.label}
                    </div>
                  </div>
                )}
                {/* Animated Bar */}
                {slot.hasData ? (
                  <div
                    className={`w-full rounded-t ${colorBg} ${isHov ? 'opacity-100' : 'opacity-75'}`}
                    style={{ 
                      height: isVisible ? `${barH}px` : '0px',
                      transition: `height 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) ${staggerDelay}s, opacity 0.2s ease`
                    }}
                  />
                ) : (
                  <div className="w-full" style={{ height: '2px', backgroundColor: 'rgba(255,255,255,0.05)', marginTop: 'auto' }} />
                )}
              </div>
            );
          })}
        </div>

        {/* Date labels */}
        <div className="relative flex" style={{ height: '18px' }}>
          {slots.map((slot, i) => (
            showLabel(i) ? (
              <div
                key={i}
                className="absolute text-[9px] text-zinc-500 -translate-x-1/2"
                style={{ left: `${(i / Math.max(totalSlots - 1, 1)) * 100}%` }}
              >
                {slot.label}
              </div>
            ) : null
          ))}
        </div>
      </div>
    );
  };


  return (
    <AppLayout breadcrumbs={[{ label: 'Dashboard' }]}>
      <Helmet>
        <title>Dashboard | ScoreCite</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="p-4 md:p-6" data-testid="dashboard-page">
        <div className="mx-auto w-full max-w-[1600px]">
        {/* Subscription Bar */}
        <Card className="bg-gradient-to-r from-[#121212] to-[#1A1A2E] border-white/10 mb-6 w-full">
          <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            {/* Pending Payment - Enterprise Quote */}
            {!isAdmin && subscription.subscription_status === 'pending_payment' && subscription.enterprise_config ? (
              <>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-yellow-500/20">
                    <Crown className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div>
                    <span className="text-white font-semibold">Enterprise Quote Ready</span>
                    <Badge className="ml-2 bg-yellow-600">Pending Payment</Badge>
                    <p className="text-zinc-400 text-xs mt-0.5">
                      £{subscription.enterprise_config.custom_price}/mo · {subscription.enterprise_config.custom_credits} credits · {subscription.enterprise_config.custom_domains} domains
                    </p>
                  </div>
                </div>
                <Link to="/pricing">
                  <Button size="sm" className="bg-purple-600 hover:bg-purple-500">
                    <Crown className="w-4 h-4 mr-1" /> Complete Payment
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    isAdmin || subscription.tier === 'pro' || subscription.tier === 'enterprise' ? 'bg-purple-500/20' :
                    subscription.tier === 'core' ? 'bg-blue-500/20' : 'bg-zinc-500/20'
                  }`}>
                    {isAdmin ? <Shield className="w-5 h-5 text-purple-400" /> :
                     subscription.tier === 'pro' || subscription.tier === 'enterprise' ? <Crown className="w-5 h-5 text-purple-400" /> :
                     subscription.tier === 'core' ? <Zap className="w-5 h-5 text-blue-400" /> :
                     <FileText className="w-5 h-5 text-zinc-400" />}
                  </div>
                  <div>
                    <span className="text-white font-semibold">{isAdmin ? 'Admin' : (tierInfo.name || 'Free')}</span>
                    <Badge className={`ml-2 ${isAdmin ? 'bg-purple-600' : subscription.subscription_status === 'active' ? 'bg-green-600' : 'bg-zinc-600'}`}>
                      {isAdmin ? 'Unlimited' : (subscription.subscription_status === 'active' ? 'Active' : 'Inactive')}
                    </Badge>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <span className="text-2xl font-bold text-white">{effectiveCredits}</span>
                    {!isAdmin && <span className="text-zinc-500">/{tierInfo.credits_monthly || 0}</span>}
                    <p className="text-zinc-500 text-xs">credits</p>
                  </div>
                  {!isAdmin && subscription.tier !== 'pro' && subscription.tier !== 'enterprise' && (
                    <Link to="/pricing">
                      <Button size="sm" className="bg-gradient-to-r from-blue-600 to-purple-600">
                        <Crown className="w-4 h-4 mr-1" /> Upgrade
                      </Button>
                    </Link>
                  )}
                  {!isAdmin && subscription.subscription_status === 'active' && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="border-white/20 text-zinc-400 hover:text-white"
                      onClick={async () => {
                        try {
                          const res = await axios.post(`${API}/billing/portal`, {
                            return_url: window.location.href
                          });
                          window.location.href = res.data.url;
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Unable to open billing portal');
                        }
                      }}
                    >
                      Manage Subscription
                    </Button>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* KPI Section */}
        {kpi.has_data && selectedDomainData && (
          <>
            {/* Domain Selector */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                <Globe className="w-5 h-5 text-zinc-400 hidden sm:block" />
                {normalizedDomains.length > 1 ? (
                  <Select value={selectedDomain} onValueChange={setSelectedDomain}>
                    <SelectTrigger className="w-40 sm:w-48 bg-[#121212] border-white/10 text-white h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-white/10">
                      {normalizedDomains.map(d => (
                        <SelectItem key={d.domain_id} value={d.domain_id} className="text-white">
                          {d.domain}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <span className="text-white font-medium text-sm truncate max-w-[200px]">{selectedDomainData.domain}</span>
                )}
                <Badge variant="outline" className="border-zinc-700 text-zinc-400 hidden md:inline-flex">
                  {filteredAudits.length} audit{filteredAudits.length !== 1 ? 's' : ''}
                </Badge>
                {normalizedDomains.length > 3 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-orange-400 hover:text-orange-300 hover:bg-orange-500/10 h-7 text-xs px-2"
                    onClick={() => setClearInactiveOpen(true)}
                  >
                    <Trash2 className="w-3 h-3 sm:mr-1" /> <span className="hidden sm:inline">Clear inactive</span>
                  </Button>
                )}
              </div>
              <div className="flex gap-2 justify-start sm:justify-end">
                {/* Core user: Change domain button */}
                {!isAdmin && subscription.tier === 'core' && (
                  <Link to="/full-audit">
                    <Button variant="outline" size="sm" className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10 h-8 text-xs px-2 sm:px-3">
                      <Globe className="w-3 h-3 sm:mr-1" /> <span className="hidden sm:inline">Change</span>
                    </Button>
                  </Link>
                )}
                {selectedDomainData.latest?.audit_id && (
                  <Link to={`/report/${selectedDomainData.latest.audit_id}`}>
                    <Button variant="outline" size="sm" className="border-white/10 text-white h-8 text-xs px-2 sm:px-3">
                      <BarChart3 className="w-3 h-3 sm:mr-1" /> <span className="hidden sm:inline">Report</span>
                    </Button>
                  </Link>
                )}
                <Link to="/full-audit">
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-500 h-8 text-xs px-2 sm:px-3">
                    <Plus className="w-3 h-3 sm:mr-1" /> <span className="hidden sm:inline">New</span>
                  </Button>
                </Link>
              </div>
            </div>

            <nav aria-label="Domain navigation" className="flex gap-1 border-b border-white/10 mb-4">
              {[['overview', 'Overview'], ['changes', 'Changes'], ['search', 'Google Search'], ['audits', 'Audits']].map(([key, label]) => <button key={key} type="button" aria-current={domainTab === key ? 'page' : undefined} onClick={() => setDomainTab(key)} className={`px-3 py-2 text-sm border-b-2 ${domainTab === key ? 'text-white border-blue-500' : 'text-zinc-400 border-transparent'}`}>{label}</button>)}
            </nav>
            {evidenceError && <p className="text-sm text-zinc-400 mb-3">{evidenceError} <button className="text-blue-400" onClick={loadDomainEvidence}>Retry</button></p>}
            {!domainEvidence && !evidenceError && domainTab !== 'audits' && <p className="text-xs text-zinc-500 mb-3">Loading stored domain evidence…</p>}
            {domainTab === 'changes' && domainEvidence && <ChangeVerification data={domainEvidence.change_verification} />}
            {domainTab === 'search' && domainEvidence && <DomainSearchConsole key={selectedDomainData.domain} domain={selectedDomainData.domain} evidence={domainEvidence} isAdmin={isAdmin} reload={loadDomainEvidence} />}
            {domainTab === 'overview' && <>
              <ChangeVerification data={domainEvidence?.change_verification} compact />
              <GoogleSearchEvidence data={domainEvidence?.search_reality} compact />
            {/* KPI Tiles Grid - auto-selects business or legacy config */}
            {(() => {
              const latest = selectedDomainData.latest || {};
              const isBusinessReport = latest.overview_score > 0;
              const KPI_CONFIG = isBusinessReport ? KPI_CONFIG_BUSINESS : KPI_CONFIG_LEGACY;
              
              // Animated KPI Tile component
              const AnimatedKPITile = ({ kpiDef, value, change, isSelected, triggerKey }) => {
                const Icon = kpiDef.icon;
                const colors = COLOR_MAP[kpiDef.color];
                const { count, ref, hasAnimated } = useCountUp(value, 800, triggerKey);
                
                return (
                  <Card
                    ref={ref}
                    className={`bg-[#121212] border cursor-pointer transition-all hover:-translate-y-0.5 ${
                      isSelected ? `${colors.border} ring-1 ring-${kpiDef.color}-500/50` : 'border-white/10 hover:border-white/20'
                    }`}
                    onClick={() => setSelectedMetric(kpiDef.key)}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <div className={`w-6 h-6 rounded flex items-center justify-center ${colors.bg}`}>
                          <Icon className={`w-3.5 h-3.5 ${colors.text}`} />
                        </div>
                        <span className="text-zinc-400 text-xs truncate">{kpiDef.label}</span>
                      </div>
                      <div className="flex items-end justify-between">
                        <span className="text-2xl font-bold text-white tabular-nums">
                          {count}{kpiDef.suffix}
                        </span>
                        <ChangeIndicator value={change} higherBetter={kpiDef.higherBetter} animate={hasAnimated} />
                      </div>
                    </CardContent>
                  </Card>
                );
              };
              
              return (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 sm:gap-3 mb-6">
                  {KPI_CONFIG.map(kpiDef => {
                    const value = latest[kpiDef.key] ?? 0;
                    const change = selectedDomainData.changes?.[kpiDef.key];
                    const isSelected = selectedMetric === kpiDef.key;
                    // Trigger key changes when domain or data changes
                    const triggerKey = `${selectedDomain}-${latest.audit_id || 'none'}`;
                    return (
                      <AnimatedKPITile
                        key={kpiDef.key}
                        kpiDef={kpiDef}
                        value={value}
                        change={change}
                        isSelected={isSelected}
                        triggerKey={triggerKey}
                      />
                    );
                  })}
                </div>
              );
            })()}

            {/* Trend Chart */}
            {selectedDomainData.timeline?.length >= 1 && (
              <Card className="bg-[#121212] border-white/10 mb-6">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white text-sm flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      {([...KPI_CONFIG_BUSINESS, ...KPI_CONFIG_LEGACY]).find(k => k.key === selectedMetric)?.label} Trend
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-zinc-500" />
                      <Select value={timeRange} onValueChange={setTimeRange}>
                        <SelectTrigger className="w-36 h-8 bg-[#0A0A0A] border-white/10 text-white text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-white/10">
                          {TIME_RANGES.map(tr => (
                            <SelectItem key={tr.value} value={tr.value} className="text-white text-xs">
                              {tr.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  {(() => {
                    const filteredTimeline = filterTimelineByRange(selectedDomainData.timeline);
                    if (filteredTimeline.length < 1) {
                      return (
                        <div className="h-16 flex items-center justify-center text-zinc-500 text-sm">
                          No data for selected time range
                        </div>
                      );
                    }
                    return (
                      <BarChart
                        data={filteredTimeline}
                        metricKey={selectedMetric}
                        color={([...KPI_CONFIG_BUSINESS, ...KPI_CONFIG_LEGACY]).find(k => k.key === selectedMetric)?.color || 'blue'}
                        range={timeRange}
                      />
                    );
                  })()}
                </CardContent>
              </Card>
            )}
            </>}
          </>
        )}

        {/* No monitored domains */}
        {!kpi.has_data && subscription.tier && subscription.tier !== 'free' && (
          <Card className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 border-blue-500/30 mb-6">
            <CardContent className="p-6 text-center">
              <Globe className="w-10 h-10 text-blue-400 mx-auto mb-2" />
              <h3 className="text-white font-semibold mb-1">Start Monitoring</h3>
              <p className="text-zinc-400 text-sm mb-3">Run your first audit to track KPIs over time</p>
              <Link to="/full-audit">
                <Button className="bg-blue-600 hover:bg-blue-500">
                  <Plus className="w-4 h-4 mr-1" /> Run First Audit
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        <div hidden={Boolean(selectedDomainData && domainTab !== 'overview' && domainTab !== 'audits')}>
        {/* Quick Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <Card className="bg-[#121212] border-white/10">
            <CardContent className="p-4 flex items-center gap-3">
              <FileText className="w-8 h-8 text-blue-400" />
              <div>
                <p className="text-zinc-500 text-xs">Total</p>
                <p className="text-xl font-bold text-white">{stats.total_audits || 0}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#121212] border-white/10">
            <CardContent className="p-4 flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-400" />
              <div>
                <p className="text-zinc-500 text-xs">Completed</p>
                <p className="text-xl font-bold text-white">{stats.completed_audits || 0}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#121212] border-white/10">
            <CardContent className="p-4 flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-purple-400" />
              <div>
                <p className="text-zinc-500 text-xs">Used</p>
                <p className="text-xl font-bold text-white">{stats.credits_used_this_month || 0}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Audits Table - Desktop / Card View - Mobile */}
        <Card className="bg-[#121212] border-white/10">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <LayoutDashboard className="w-4 h-4" /> 
                {selectedDomain ? `Audits for ${selectedDomain}` : 'Recent Audits'}
              </CardTitle>
              {selectedDomain && filteredAudits.length !== data?.audits?.length && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-zinc-400 text-xs h-7"
                  onClick={() => setSelectedDomain(null)}
                >
                  Show all ({data?.audits?.length})
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {(selectedDomain ? filteredAudits : data?.audits)?.length > 0 ? (
              <>
                {/* Mobile Cards - hidden on desktop */}
                <div className="md:hidden space-y-3">
                  {(selectedDomain ? filteredAudits : data?.audits || []).map(audit => (
                    <div key={audit.id} className="rounded-lg border border-white/10 bg-[#1A1A1A] p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-white font-medium truncate">{audit.target_url}</div>
                          <div className="text-xs text-zinc-500 mt-1">
                            {audit.created_at ? new Date(audit.created_at).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                          </div>
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <Badge variant="outline" className={audit.report_mode === 'paid_full' ? 'border-green-500 text-green-400 text-xs' : 'border-zinc-700 text-zinc-400 text-xs'}>
                              {audit.report_mode === 'paid_full' ? 'Full' : 'Free'}
                            </Badge>
                            <Badge className={`text-xs ${audit.status === 'completed' ? 'bg-green-600' : audit.status === 'failed' ? 'bg-red-600' : 'bg-blue-600'}`}>
                              {audit.status === 'completed' ? 'Ready' : audit.status === 'failed' ? 'Failed' : 'Running'}
                            </Badge>
                          </div>
                        </div>
                        <div className="shrink-0 flex items-center gap-1">
                          {audit.status === 'completed' ? (
                            <Link to={`/report/${audit.id}`}>
                              <Button variant="ghost" size="sm" className="text-blue-400 h-8 px-2 text-xs" data-testid="view-report-btn-mobile">View</Button>
                            </Link>
                          ) : audit.status !== 'failed' && (
                            <Link to={`/audit/${audit.id}`}>
                              <Button variant="ghost" size="sm" className="text-zinc-400 h-8 px-2 text-xs">Track</Button>
                            </Link>
                          )}
                          {audit.status === 'completed' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 h-8 w-8 p-0"
                              onClick={() => handleReaudit(audit.id)}
                              disabled={reauditing === audit.id}
                              title="Re-run this audit"
                            >
                              <RefreshCw className={`w-3.5 h-3.5 ${reauditing === audit.id ? 'animate-spin' : ''}`} />
                            </Button>
                          )}
                          {audit.is_locked && (
                            <Lock className="w-3.5 h-3.5 text-yellow-400" title={`Protected: ${audit.lock_reason || 'Locked'}`} />
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className={audit.is_locked ? "text-zinc-600 cursor-not-allowed h-8 w-8 p-0" : "text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0"}
                            onClick={() => !audit.is_locked && setDeleteAudit(audit)}
                            disabled={audit.is_locked}
                            title={audit.is_locked ? 'Protected - cannot delete' : 'Delete audit'}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop Table - hidden on mobile */}
                <div className="hidden md:block">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10">
                        <TableHead className="text-zinc-400 w-24">ID</TableHead>
                        <TableHead className="text-zinc-400">Domain</TableHead>
                        <TableHead className="text-zinc-400">Type</TableHead>
                        <TableHead className="text-zinc-400">Status</TableHead>
                        <TableHead className="text-zinc-400">Date</TableHead>
                        <TableHead className="text-zinc-400 text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(selectedDomain ? filteredAudits : data?.audits || []).map(audit => (
                        <TableRow key={audit.id} className="border-white/10">
                          <TableCell className="text-zinc-500 font-mono text-xs">
                            <div className="flex items-center gap-1">
                              <span>{audit.id?.substring(0, 8)}...</span>
                              <button 
                                onClick={() => { navigator.clipboard.writeText(audit.id); toast.success('Audit ID copied'); }}
                                className="text-zinc-600 hover:text-zinc-300 transition-colors"
                                title="Copy full ID"
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                            </div>
                          </TableCell>
                          <TableCell className="text-white font-medium text-sm">{audit.target_url}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={audit.report_mode === 'paid_full' ? 'border-green-500 text-green-400' : 'border-zinc-700 text-zinc-400'}>
                              {audit.report_mode === 'paid_full' ? 'Full' : 'Free'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={audit.status === 'completed' ? 'bg-green-600' : audit.status === 'failed' ? 'bg-red-600' : 'bg-blue-600'}>
                              {audit.status === 'completed' ? 'Ready' : audit.status === 'failed' ? 'Failed' : 'Running'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-zinc-400 text-sm">
                            {audit.created_at ? new Date(audit.created_at).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {audit.status === 'completed' ? (
                                <Link to={`/report/${audit.id}`}>
                                  <Button variant="ghost" size="sm" className="text-blue-400 h-7 text-xs" data-testid="view-report-btn">View</Button>
                                </Link>
                              ) : audit.status !== 'failed' && (
                                <Link to={`/audit/${audit.id}`}>
                                  <Button variant="ghost" size="sm" className="text-zinc-400 h-7 text-xs">Track</Button>
                                </Link>
                              )}
                              {audit.status === 'completed' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 h-7 w-7 p-0"
                                  onClick={() => handleReaudit(audit.id)}
                                  disabled={reauditing === audit.id}
                                  title="Re-run this audit and compare with the previous result"
                                  data-testid="reaudit-btn"
                                >
                                  <RefreshCw className={`w-3.5 h-3.5 ${reauditing === audit.id ? 'animate-spin' : ''}`} />
                                </Button>
                              )}
                              {audit.is_locked && (
                                <Lock className="w-3.5 h-3.5 text-yellow-400" title={`Protected: ${audit.lock_reason || 'Locked'}`} />
                              )}
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className={audit.is_locked ? "text-zinc-600 cursor-not-allowed h-7 w-7 p-0" : "text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 w-7 p-0"}
                                onClick={() => !audit.is_locked && setDeleteAudit(audit)}
                                disabled={audit.is_locked}
                                title={audit.is_locked ? 'Protected - cannot delete' : 'Delete audit'}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <FileText className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
                <p className="text-zinc-400 text-sm mb-3">No audits yet</p>
                <Link to="/full-audit">
                  <Button size="sm" className="bg-blue-600">
                    <Plus className="w-4 h-4 mr-1" /> Run first audit
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contact Support */}
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <h3 className="text-white font-medium">Need help?</h3>
              <p className="text-zinc-500 text-sm">Contact us for support or feedback</p>
            </div>
            <ContactButton />
          </CardContent>
        </Card>
        </div>

        </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteAudit} onOpenChange={(open) => !open && setDeleteAudit(null)}>
        <AlertDialogContent className="bg-[#121212] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Audit</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Are you sure you want to delete this audit for <span className="text-white font-medium">{deleteAudit?.target_url}</span>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 text-white border-white/10 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteAudit}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-500 text-white"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Clear Inactive Domains Dialog */}
      <AlertDialog open={clearInactiveOpen} onOpenChange={setClearInactiveOpen}>
        <AlertDialogContent className="bg-[#121212] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Clear Inactive Domains</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This will remove all domains from the dropdown that have no associated audits. Domains with at least one audit will be kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 text-white border-white/10 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleClearInactive}
              disabled={clearingInactive}
              className="bg-orange-600 hover:bg-orange-500 text-white"
            >
              {clearingInactive ? 'Clearing...' : 'Clear Inactive'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </AppLayout>
  );
}
